import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import InlineText from '@editor/droplets/_bubble-options/components/inline-text';
import DropletOptions from '../_bubble-options';
import TabsOptions from './components/_edit-options';
import TabContent from './components/tab-content';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('tabs', {
  template: require('./template.html'),
  mixins: [cssGenerator],
  computed: {
    ...mapGetters([
      'currentPage',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalToolBarOpen',
    ]),
    profile() {
      return this._dropletType(this.droplet);
    },
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    hideTransform() {
      return this.globalBubbleOptionsOpen ||
      this.globalToolBarOpen ||
      this.dragging ||
      (this.globalBubbleOpen && !this.showEditBubble);
    },
    getTransformStyle() {
      let style = null;
      if (this.globalToolBarOpen) {
        style = 'initial';
      }
      if (!this.globalBubbleOpen && this.display.perspective) {
        style = 'preserve-3d';
      }
      return style;
    },
    routeWatch() {
      const r = this.$store.getters.route;
      if (r === 'page-editor') {
        this.unBindPlugin();
        this.bindPlugin();
      }
      return r;
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzTabs' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
    lastRowMargin() {
      const selector = _.find(this.droplet.mediaQueries, {
        selector: dropletSchema.lastRowSelector,
      });
      const property = selector ? _.find(selector.properties, { property: 'margin-bottom' }) : false;
      return property;
    },
  },
  data() {
    const renderKey = Date.now();
    return {
      type: dropletSchema.type,
      renderKey,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        transform: null,
        perspective: null,
        'padding-left': null,
        'padding-right': null,
        'padding-top': null,
        'padding-bottom': null,
        'margin-bottom': null,
      },
      hasActiveChild: false,
      hidden: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-normal',
      showEditBubble: false,
      openToolBar: false,
      toolbar: [],
      showRevealClass: false,
    };
  },
  name: 'tabs',
  props: ['droplet', 'items', 'index'],
  components: {
    DropletOptions,
    TabsOptions,
    TabContent,
    InlineText,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzTabs',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
      this.unBindPlugin();
      this.bindPlugin();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
    'activeDroplet._id': function checkDroplets(id) {
      if (id && this.droplet._id !== id) {
        const activeDroplets = this.$el.querySelectorAll(`#drp-${id}`);
        if (activeDroplets.length > 0) {
          this.hasActiveChild = true;
        }
      } else {
        this.hasActiveChild = false;
        // bounce droplet plugin when stylebar opens
        this.unBindPlugin();
        this.bindPlugin();
      }
      if (!id) {
        this.showRevealClass = false;
      }
    },
  },
  created() {
    this.checkNewValues();
  },
  mounted() {
    this.callCssGenerator();
    $(document).ready(this.bindPlugin);
    this.observeVisibility();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    checkNewValues() {
      if (!this.lastRowMargin) {
        this.$store.dispatch('changeDropletCss', {
          drp: this.droplet,
          val: '0px',
          property: 'margin-bottom',
          selector: dropletSchema.lastRowSelector,
          viewport: this.viewport,
        });
        this.callCssGenerator();
      }
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    handleToolbar(v) {
      this.openToolBar = v;
      this.showEditBubble = false;
    },
    updateText(text, tab) {
      const payload = {};
      payload.tab = tab;
      payload.title = text;
      payload.title = text.trim() === '' ? false : text;
      if (tab.title !== text) {
        this.$store.dispatch('setTabTitle', payload);
      }
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    bindPlugin() {
      const $tabs = $(this.$refs.drzTabs);
      $tabs.drzTabs();
    },
    unBindPlugin() {
      const $tabs = $(this.$refs.drzTabs);
      if ($tabs.drzTabs.destroy) {
        $tabs.drzTabs.destroy($tabs);
      }
    },
    toggleBubble() {
      if (!this.roamingTemplate && !this.openToolBar) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
    async onDrop(index) {
      this.renderKey = Date.now();
      await this._timeBuffer(250);
      this.unBindPlugin();
      this.bindPlugin();
      if (index > 0) {
        const $tabs = $(this.$refs.drzTabs);
        const tab = `#tabs-${this.droplet._id}-${index}`;
        $tabs.find(tab).trigger('click');
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/tabs/index.js